import { MuiColorInput, MuiColorInputColors } from "mui-color-input";

import {
  Box,
  Button,
  Checkbox,
  Drawer,
  FormControlLabel,
  IconButton,
  MenuItem,
  Slider,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import * as React from "react";
import { useLocalRampState } from "../RampEditorPresenter";
import { Shape, SpotMetadata, ZoneMetadata, LineMetadata } from "./RampCanvas";
import { smoothLine } from "./math";
import { FILL_PATTERNS } from "./patterns/usePatterns";
import { updateAtId } from "./utils";
import { uuidv4 } from "../../../../../utils";
import { SpotPropertiesSection } from "./SpotPropertiesSection";
import { detectRequiredSpots } from "./utils/gapSpotDetection";
import { TabPanel } from "../../../../../components/TabPanel";

export const TAG_OPTIONS = ["hangar", "parkable", "obstruction", "utilization", "reference_line", "spot", "zone", "egress_lane"];

export const PREFIXED_COLORS = [
  {
    label: "Reference Line",
    stroke_style: "#00CED1",
    fill_style: "rgba(0, 206, 209, 0.10)",
  },
  {
    label: "Parking Area",
    stroke_style: "#3c6ce9",
    fill_style: "rgba(238, 239, 243, 0.10)",
  },
  {
    label: "Hazard Area",
    stroke_style: "#FF0000",
    fill_style: "#FF000030",
  },
  {
    label: "Taxi / Runway Area",
    stroke_style: "#eed202",
    fill_style: "#eed20230",
  },
  {
    label: "Free Space Area",
    stroke_style: "#00FF00",
    fill_style: "rgba(0, 255, 0, 0.38)",
  },
  {
    label: "Ground Vehicles Area",
    stroke_style: "#FCFAFF",
    fill_style: "#FCFAFF30",
  },
  {
    label: "Taxi Line",
    stroke_style: "#eed202",
    fill_style: "#eed20230",
  },
  {
    label: "Runway Lines",
    stroke_style: "#FFFFFF",
    fill_style: "#FFFFFF30",
  },
  {
    label: "Hangar Area",
    fill_style: "#c8cfde50",
    stroke_style: "#3e5d8c",
  },
];

export const SHAPE_COLORS = [
  "rgba(255, 0, 0, 0.5)",
  "rgba(255, 255, 0, 0.5)",
  "rgba(0, 255, 0, 0.5)",
  "rgba(0, 0, 255, 0.5)",
];

export const TEXT_COLORS = [
  "black",
  "white",
  "red",
  "green",
  "blue",
  "yellow",
  "purple",
  "orange",
  "pink",
  "brown",
];

export const LINE_STYLES = ["solid", "dashed", "dotted"];

export const TEXT_POSITION_OPTIONS = [
  { value: 'start', label: 'Start' },
  { value: 'middle', label: 'Middle' },
  { value: 'end', label: 'End' },
] as const;

type Props = {
  contextMenu: {
    x: number;
    y: number;
    shapeId: string;
  } | null;
  setContextMenu: (contextMenu: Props["contextMenu"]) => void;
  onClose: () => void;
  shapes: Shape[];
  setShapes: (shapes: Shape[]) => void;
  canvasHeight: number;
  canvasWidth: number;
  previewSpots: Shape[];
  setPreviewSpots: (spots: Shape[]) => void;
};

export const ShapeEditDialog: React.FC<Props> = ({
  contextMenu,
  setContextMenu,
  onClose,
  shapes,
  setShapes,
  canvasHeight,
  canvasWidth,
  previewSpots,
  setPreviewSpots,
}) => {
  const { activeShapes, setActiveShapes, ramp } = useLocalRampState();
  const [activeTab, setActiveTab] = React.useState(0);

  const shape = React.useMemo(
    () => shapes.find((s) => s.id === contextMenu?.shapeId),
    [shapes, contextMenu?.shapeId]
  );

  // Calculate the total number of tabs and reset if out of bounds
  React.useEffect(() => {
    // Early exit if no shape
    if (!shape) return;

    const showAlgorithmTab =
      shape?.tags?.includes("parkable") ||
      shape?.tags?.includes("reference_line") ||
      shape?.tags?.includes("spot") ||
      shape?.type === "Spot" ||
      shape?.type === "Zone";

    const totalTabs = showAlgorithmTab ? 3 : 2;

    if (activeTab >= totalTabs) {
      setActiveTab(totalTabs - 1);
    }
  }, [shape, activeTab]);

  // Keyboard shortcuts for tab switching
  React.useEffect(() => {
    if (!contextMenu || !shape) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Ctrl+1/2/3 (or Cmd on Mac)
      if (e.ctrlKey || e.metaKey) {
        const showAlgorithmTab =
          shape?.tags?.includes("parkable") ||
          shape?.tags?.includes("reference_line") ||
          shape?.tags?.includes("spot") ||
          shape?.type === "Spot" ||
          shape?.type === "Zone";

        const totalTabs = showAlgorithmTab ? 3 : 2;

        if (e.key === "1") {
          e.preventDefault();
          setActiveTab(0);
        } else if (e.key === "2") {
          e.preventDefault();
          setActiveTab(1);
        } else if (e.key === "3" && totalTabs >= 3) {
          e.preventDefault();
          setActiveTab(2);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [contextMenu, shape]);

  const setSelectedShapes = (attribute: string, value: any) => {
    const updatedShapes = shapes.map((shape) => {
      if (
        activeShapes.includes(shape.id) ||
        shape.id === contextMenu?.shapeId
      ) {
        return { ...shape, [attribute]: value };
      }
      return shape;
    });
    setShapes(updatedShapes);
  };

  const setSelectedShapesMultiple = (attributes: Record<string, any>) => {
    const updatedShapes = shapes.map((shape) => {
      if (
        activeShapes.includes(shape.id) ||
        shape.id === contextMenu?.shapeId
      ) {
        return { ...shape, ...attributes };
      }
      return shape;
    });
    setShapes(updatedShapes);
  };

  if (!shape) {
    return null;
  }

  const prefixedValue = PREFIXED_COLORS.find(
    (option) =>
      option.stroke_style === shape.stroke_style &&
      option.fill_style === shape.fill_style
  )?.label;

  // Determine if Algorithm tab should be shown
  const showAlgorithmTab =
    shape?.tags?.includes("parkable") ||
    shape?.tags?.includes("reference_line") ||
    shape?.tags?.includes("spot") ||
    shape?.type === "Spot" ||
    shape?.type === "Zone";

  return (
    <Drawer
      open={!!contextMenu}
      onClose={onClose}
      anchor="left"
      hideBackdrop={false}
      ModalProps={{
        BackdropProps: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.1)', // Very light - 10% opacity
          }
        }
      }}
    >
      <Box sx={{ width: 400, height: '100vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header with title and tabs - fixed */}
        <Box sx={{ flexShrink: 0 }}>
          <Typography variant="h6" sx={{ p: 2, pb: 0 }}>
            Shape Properties
          </Typography>

          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}
          >
            <Tab label="Properties (⌘1)" />
            <Tab label="Styling (⌘2)" />
            {showAlgorithmTab && <Tab label="Algorithm (⌘3)" />}
          </Tabs>
        </Box>

        {/* Scrollable tab content area */}
        <Box sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden' }}>

        {/* Tab 0: Properties */}
        <TabPanel value={activeTab} index={0}>
          <Stack direction="column" spacing={2}>
          <FormControlLabel
            control={<Switch checked={shape?.locked ?? false} />}
            onChange={(evt) => setSelectedShapes("locked", !shape?.locked)}
            label="Locked?"
          />
          <TextField
            size="small"
            fullWidth
            label="Tags"
            select
            onChange={(event) => {
              const value = event.target.value;
              setSelectedShapes(
                "tags",
                typeof value === "string" ? value.split(",") : value
              );
            }}
            value={shape?.tags ?? []}
            SelectProps={{
              multiple: true,
              renderValue: (selected) =>
                Array.isArray(selected) ? selected.join(", ") : selected,
              MenuProps: {
                PaperProps: {
                  sx: { zIndex: 9999 },
                },
              },
            }}
          >
            {TAG_OPTIONS.map((tag) => (
              <MenuItem key={tag} value={tag}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={shape?.tags?.includes(tag) || false}
                      onChange={(e) => {
                        const currentTags = [...(shape?.tags || [])];
                        if (e.target.checked) {
                          if (!currentTags.includes(tag)) {
                            setSelectedShapes("tags", [...currentTags, tag]);
                          }
                        } else {
                          setSelectedShapes(
                            "tags",
                            currentTags.filter((t) => t !== tag)
                          );
                        }
                      }}
                    />
                  }
                  label={tag}
                  onClick={(e) => e.stopPropagation()}
                />
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Text"
            fullWidth
            onChange={(event) => {
              setSelectedShapes("text", event.target.value);
            }}
            value={shape?.text || ""}
          />
          </Stack>
        </TabPanel>

        {/* Tab 1: Styling */}
        <TabPanel value={activeTab} index={1}>
          <Stack direction="column" spacing={2}>
          <TextField
            size="small"
            fullWidth
            label="Pre-fixed Colors"
            select
            onChange={(event) => {
              const option = PREFIXED_COLORS.find(
                (option) => option.label === event.target.value
              );
              setSelectedShapesMultiple({
                stroke_style: option?.stroke_style,
                fill_style: option?.fill_style,
                fill_pattern: "",
              });
            }}
            sx={{ zIndex: 9999 }}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: { zIndex: 9999 }, // Ensure Menu aligns with Drawer
                },
              },
            }}
            value={prefixedValue ?? "custom"}
          >
            {PREFIXED_COLORS.map((option) => (
              <MenuItem key={option.label} value={option.label}>
                {option.label}
              </MenuItem>
            ))}
            <MenuItem value="custom">Custom</MenuItem>
          </TextField>
          {/* select color */}
          <Typography sx={{ mt: 1 }}>Fill</Typography>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <MuiColorInput
              fullWidth
              size="small"
              label="Color"
              isAlphaHidden={false}
              onChange={(value: string, colors: MuiColorInputColors) => {
                setSelectedShapesMultiple({
                  fill_style: colors.hex8,
                  fill_pattern: "",
                });
              }}
              value={shape?.fill_style ?? ""}
            />
            <TextField
              size="small"
              fullWidth
              label="Pattern"
              select
              onChange={(event) => {
                setSelectedShapesMultiple({
                  fill_style: "",
                  fill_pattern: event.target.value,
                });
              }}
              value={shape?.fill_pattern || ""}
            >
              {FILL_PATTERNS().map((option) => (
                <MenuItem key={option.label} value={option.label}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <img
                      src={option.value}
                      style={{
                        width: 24,
                        height: 24,
                        border: `1px solid darkgrey`,
                      }}
                    />
                    <Typography variant="inherit">{option.label}</Typography>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <Typography sx={{ mt: 1 }}>Line</Typography>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <MuiColorInput
              fullWidth
              size="small"
              label="Color"
              isAlphaHidden={false}
              onChange={(value: string, colors: MuiColorInputColors) => {
                setSelectedShapes("stroke_style", colors.hex8);
              }}
              value={shape?.stroke_style ?? ""}
            />
            {/* line style */}
            <TextField
              size="small"
              fullWidth
              label="Line Style"
              select
              value={shape?.line_style || "solid"}
              onChange={(event) => {
                setSelectedShapes("line_style", event.target.value);
              }}
            >
              {[...LINE_STYLES].map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Line Width"
              inputProps={{ min: 1, max: 20, step: 1 }}
              onChange={(event) => {
                setSelectedShapes("line_width", Number(event.target.value));
              }}
              value={shape?.line_width ?? 1}
            />
          </Stack>
          <Typography sx={{ mt: 1 }}>Text</Typography>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Font Size"
              inputProps={{ min: 10, max: 64, step: 1 }}
              onChange={(event) => {
                setSelectedShapes("font_size", Number(event.target.value));
              }}
              value={shape?.font_size ?? 12}
            />
          </Stack>

          {/* Text Label Controls - Line shapes with text only */}
          {shape?.type === "Line" && shape?.text && (
            <>
              <FormControlLabel
                control={
                  <Switch
                    checked={(shape.metadata as LineMetadata)?.show_text !== false}
                    onChange={(e) => {
                      const updatedShapes = shapes.map((s) => {
                        if (activeShapes.includes(s.id) || s.id === contextMenu?.shapeId) {
                          return {
                            ...s,
                            metadata: { ...s.metadata, show_text: e.target.checked },
                          };
                        }
                        return s;
                      });
                      setShapes(updatedShapes);
                    }}
                  />
                }
                label="Show Text"
              />
              <TextField
                fullWidth
                size="small"
                select
                label="Text Position"
                value={(shape.metadata as LineMetadata)?.text_position || "middle"}
                onChange={(e) => {
                  const updatedShapes = shapes.map((s) => {
                    if (activeShapes.includes(s.id) || s.id === contextMenu?.shapeId) {
                      return {
                        ...s,
                        metadata: { ...s.metadata, text_position: e.target.value },
                      };
                    }
                    return s;
                  });
                  setShapes(updatedShapes);
                }}
                SelectProps={{ MenuProps: { PaperProps: { sx: { zIndex: 9999 } } } }}
              >
                {TEXT_POSITION_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
            </>
          )}
          </Stack>
        </TabPanel>

        {/* Tab 2 (conditional): Algorithm */}
        {showAlgorithmTab && (
          <TabPanel value={activeTab} index={2}>
            <Stack direction="column" spacing={2}>

          {/* Algorithm-specific fields */}
          {(shape?.tags?.includes("parkable") || shape?.tags?.includes("reference_line")) && (
            <>
              <Typography sx={{ mt: 2 }}>Algorithm Settings</Typography>
              
              {/* Priority field for both parkable areas and reference lines */}
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" gutterBottom>
                  Priority (0-10, higher = preferred)
                </Typography>
                <Slider
                  value={shape?.priority ?? 5}
                  onChange={(_, value) => setSelectedShapes("priority", value)}
                  min={0}
                  max={10}
                  step={0.1}
                  marks={[
                    { value: 0, label: '0' },
                    { value: 5, label: '5' },
                    { value: 10, label: '10' },
                  ]}
                  valueLabelDisplay="auto"
                />
              </Box>
              
              {/* Orientation field only for reference lines */}
              {shape?.tags?.includes("reference_line") && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    Aircraft Orientation (select all that apply)
                  </Typography>
                  <Stack direction="column" spacing={1}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={(shape?.orientation || []).includes("nose_to_line")}
                          onChange={(e) => {
                            const currentOrientations = shape?.orientation || [];
                            if (e.target.checked) {
                              if (!currentOrientations.includes("nose_to_line")) {
                                setSelectedShapes("orientation", [...currentOrientations, "nose_to_line"]);
                              }
                            } else {
                              setSelectedShapes("orientation", currentOrientations.filter(o => o !== "nose_to_line"));
                            }
                          }}
                        />
                      }
                      label="Nose to Line"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={(shape?.orientation || []).includes("tail_to_line")}
                          onChange={(e) => {
                            const currentOrientations = shape?.orientation || [];
                            if (e.target.checked) {
                              if (!currentOrientations.includes("tail_to_line")) {
                                setSelectedShapes("orientation", [...currentOrientations, "tail_to_line"]);
                              }
                            } else {
                              setSelectedShapes("orientation", currentOrientations.filter(o => o !== "tail_to_line"));
                            }
                          }}
                        />
                      }
                      label="Tail to Line"
                    />
                  </Stack>
                </Box>
              )}
            </>
          )}

          {/* Spot reference points */}
          {shape?.tags?.includes("spot") && (
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                Reference Points & Orientations
              </Typography>
              <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ py: 0.5, px: 1 }}></TableCell>
                    <TableCell align="center" sx={{ py: 0.5, px: 0.5, fontSize: '0.75rem' }}>
                      Nose
                    </TableCell>
                    <TableCell align="center" sx={{ py: 0.5, px: 0.5, fontSize: '0.75rem' }}>
                      Tail
                    </TableCell>
                    <TableCell align="center" sx={{ py: 0.5, px: 0.5, fontSize: '0.75rem' }}>
                      Center
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    { name: 'north' as const, label: 'North', tooltip: 'Top edge center' },
                    { name: 'south' as const, label: 'South', tooltip: 'Bottom edge center' },
                    { name: 'east' as const, label: 'East', tooltip: 'Right edge center' },
                    { name: 'west' as const, label: 'West', tooltip: 'Left edge center' },
                    { name: 'center' as const, label: 'Center', tooltip: 'Centroid of the spot' },
                  ].map((refPoint) => {
                    const handleOrientationChange = (orientation: 'nose_to_line' | 'tail_to_line' | 'center', checked: boolean) => {
                      const currentRefPoints = [...(shape?.reference_points || [])];
                      const index = currentRefPoints.findIndex(rp => rp.name === refPoint.name);

                      if (checked) {
                        if (index >= 0) {
                          const currentOrientations = currentRefPoints[index].validOrientations;
                          if (!currentOrientations.includes(orientation)) {
                            currentRefPoints[index].validOrientations = [...currentOrientations, orientation];
                          }
                        } else {
                          currentRefPoints.push({ name: refPoint.name, validOrientations: [orientation] });
                        }
                      } else {
                        if (index >= 0) {
                          const newOrientations = currentRefPoints[index].validOrientations.filter(o => o !== orientation);
                          if (newOrientations.length === 0) {
                            currentRefPoints.splice(index, 1);
                          } else {
                            currentRefPoints[index].validOrientations = newOrientations;
                          }
                        }
                      }
                      setSelectedShapes('reference_points', currentRefPoints);
                    };

                    const refPointData = (shape?.reference_points || []).find(rp => rp.name === refPoint.name);
                    const isNoseChecked = refPointData?.validOrientations.includes('nose_to_line') || false;
                    const isTailChecked = refPointData?.validOrientations.includes('tail_to_line') || false;
                    const isCenterChecked = refPointData?.validOrientations.includes('center') || false;

                    return (
                      <TableRow key={refPoint.name}>
                        <TableCell sx={{ py: 0.5, px: 1 }}>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography variant="body2">{refPoint.label}</Typography>
                            <Tooltip title={refPoint.tooltip} placement="right">
                              <IconButton size="small" sx={{ p: 0 }}>
                                <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, px: 0.5 }}>
                          {refPoint.name !== 'center' ? (
                            <Checkbox
                              size="small"
                              checked={isNoseChecked}
                              onChange={(e) => handleOrientationChange('nose_to_line', e.target.checked)}
                              sx={{ p: 0 }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.disabled">-</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, px: 0.5 }}>
                          {refPoint.name !== 'center' ? (
                            <Checkbox
                              size="small"
                              checked={isTailChecked}
                              onChange={(e) => handleOrientationChange('tail_to_line', e.target.checked)}
                              sx={{ p: 0 }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.disabled">-</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5, px: 0.5 }}>
                          {refPoint.name === 'center' ? (
                            <Checkbox
                              size="small"
                              checked={isCenterChecked}
                              onChange={(e) => handleOrientationChange('center', e.target.checked)}
                              sx={{ p: 0 }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.disabled">-</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}

          {/* Spot properties for temporal parking algorithm */}
          {shape?.type === "Spot" && (
            <SpotPropertiesSection
              shape={shape}
              shapes={shapes}
              activeShapes={activeShapes}
              contextMenu={contextMenu}
              setShapes={setShapes}
              canvasWidth={canvasWidth}
              previewSpots={previewSpots}
              setPreviewSpots={setPreviewSpots}
            />
          )}

          {/* Zone properties for temporal parking algorithm */}
          {shape?.type === "Zone" && (
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Zone Properties
              </Typography>
              <Stack spacing={2} sx={{ mt: 1 }}>
                {/* Disabled Toggle */}
                <FormControlLabel
                  control={
                    <Switch
                      checked={(shape.metadata as ZoneMetadata)?.disabled || false}
                      onChange={(e) => {
                        const updatedShapes = shapes.map((s) => {
                          if (activeShapes.includes(s.id) || s.id === contextMenu?.shapeId) {
                            return {
                              ...s,
                              metadata: {
                                ...s.metadata,
                                disabled: e.target.checked,
                              },
                            };
                          }
                          return s;
                        });
                        setShapes(updatedShapes);
                      }}
                    />
                  }
                  label={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography variant="body2">
                        Disabled (exclude all spots in zone)
                      </Typography>
                      <Tooltip title="When enabled, all spots in this zone will be unavailable to the parking algorithm. Use for closing entire zones during maintenance or construction.">
                        <IconButton size="small" sx={{ p: 0 }}>
                          <InfoOutlinedIcon
                            sx={{ fontSize: 14, color: "text.secondary" }}
                          />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  }
                />

                {/* Staging Area Toggle */}
                <FormControlLabel
                  control={
                    <Switch
                      checked={(shape.metadata as ZoneMetadata)?.is_staging || false}
                      onChange={(e) => {
                        const updatedShapes = shapes.map((s) => {
                          if (activeShapes.includes(s.id) || s.id === contextMenu?.shapeId) {
                            return {
                              ...s,
                              metadata: {
                                ...s.metadata,
                                is_staging: e.target.checked,
                              },
                            };
                          }
                          return s;
                        });
                        setShapes(updatedShapes);
                      }}
                    />
                  }
                  label={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography variant="body2">
                        Staging Area
                      </Typography>
                      <Tooltip title="Mark this zone as a staging area for aircraft servicing. When staging workflow is enabled, long-stay aircraft will use staging zones for arrival/departure servicing operations before/after permanent parking. Typical use: areas near fuel trucks, maintenance facilities, or passenger terminals.">
                        <IconButton size="small" sx={{ p: 0 }}>
                          <InfoOutlinedIcon
                            sx={{ fontSize: 14, color: "text.secondary" }}
                          />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  }
                />

                <TextField
                  size="small"
                  fullWidth
                  label="Zone Name"
                  value={shape?.text || ""}
                  onChange={(e) => setSelectedShapes("text", e.target.value)}
                  placeholder="e.g., A, B, North, Transient"
                  helperText="Zone name used for spot naming (e.g., 'A' → A01, A02)"
                />
                <Box>
                  <Typography variant="body2" gutterBottom>
                    Priority (0-10, higher = preferred)
                  </Typography>
                  <Slider
                    value={shape?.priority ?? 5}
                    onChange={(_, value) => setSelectedShapes("priority", value)}
                    min={0}
                    max={10}
                    step={1}
                    marks={[
                      { value: 0, label: '0' },
                      { value: 5, label: '5' },
                      { value: 10, label: '10' },
                    ]}
                    valueLabelDisplay="auto"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Higher priority zones preferred in temporal parking
                  </Typography>
                </Box>
              </Stack>
            </Box>
          )}
            </Stack>
          </TabPanel>
        )}
        </Box>

        {/* Actions - Always visible at bottom (sticky) */}
        <Box sx={{ p: 2, borderTop: 1, borderColor: "divider", flexShrink: 0, bgcolor: 'background.paper' }}>
          <Stack direction="row" justifyContent="space-between" width="100%">
            <Button
              color="error"
              onClick={() => {
                // delete this shape and all selected shapes
                const updatedShapes = shapes.filter(
                  (s) =>
                    !activeShapes.includes(s.id) && s.id !== contextMenu.shapeId
                );
                setActiveShapes([]);
                setShapes(updatedShapes);
                setContextMenu(null);
              }}
            >
              Delete
            </Button>
            <Stack direction="row" spacing={1}>
              <Button
                onClick={() => {
                  // Ensure we have a valid ramp context
                  if (!ramp || !ramp.id || !ramp.fbo_id) {
                    console.error("Cannot copy shape - ramp not properly loaded");
                    return;
                  }
                  
                  const updatedShapes = [...shapes];
                  updatedShapes.push({ 
                    ...shape,
                    id: uuidv4(), // Generate new ID for the copied shape
                    ramp_id: ramp.id, // Explicitly use current ramp's ID
                    fbo_id: ramp.fbo_id // Explicitly use current FBO's ID
                  });
                  setShapes(updatedShapes);
                }}
              >
                Copy
              </Button>
              <Button onClick={onClose}>Cancel</Button>
              <Button
                variant="contained"
                onClick={() => {
                  // Re-detect required spots if this is a gap spot
                  if (shape?.type === "Spot" && (shape.metadata as SpotMetadata)?.isGapSpot) {
                    const requiredSpotIds = detectRequiredSpots(shape, shapes);
                    const updatedShapes = shapes.map((s) => {
                      if (s.id === shape.id) {
                        return {
                          ...s,
                          metadata: {
                            ...s.metadata,
                            requiresOccupiedSpots: requiredSpotIds,
                          },
                        };
                      }
                      return s;
                    });
                    setShapes(updatedShapes);
                  }
                  onClose();
                }}
              >
                OK
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Drawer>
  );
};
